import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, Wallet } from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";
import { PARTNER_KIT_LABELS, labelFor } from "@/lib/kitMethodLabels";
import { TankCleanContext } from "@/components/TankCleanContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-b-0">
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 w-44 shrink-0">{label}</div>
    <div className="text-sm text-slate-800 break-words flex-1">{children}</div>
  </div>
);

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return "—"; }
}

// ── Ledger row helpers (kept in sync with Ledger.jsx) ──────────────────
const LEDGER_STATUS_STYLE = {
  pending:          { pill: "bg-amber-100 text-amber-800",     dot: "bg-amber-500" },
  approved:         { pill: "bg-sky-100 text-sky-700",         dot: "bg-sky-500" },
  payout_scheduled: { pill: "bg-indigo-100 text-indigo-700",   dot: "bg-indigo-500" },
  paid:             { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  void:             { pill: "bg-slate-100 text-slate-500",     dot: "bg-slate-400" },
};

const LEDGER_STATUS_LABEL = {
  pending:          "pending",
  approved:         "approved",
  payout_scheduled: "payout scheduled",
  paid:             "paid",
  void:             "void",
};

const LEDGER_TYPE_LABEL = {
  commission:            "Partner commission",
  adjustment:            "Adjustment",
  postage_reimbursement: "Postage reimbursement",
};

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2,
});

function LedgerStatusPill({ status }) {
  const style = LEDGER_STATUS_STYLE[status] || LEDGER_STATUS_STYLE.void;
  const label = LEDGER_STATUS_LABEL[status] || status || "—";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

export default function PartnerBookingDetail() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [ledger, setLedger] = useState(null);      // { rows: [] }
  const [ledgerError, setLedgerError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/partner/bookings/${encodeURIComponent(reference)}`);
        if (!cancelled) setB(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "Booking not found." : (e?.response?.data?.detail || "Unable to load booking"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reference]);

  // Fetch ledger rows scoped to this booking reference.
  // Rendered in a small "Ledger lines for this booking" panel below.
  // Independent from the main booking fetch so a ledger failure never
  // hides the booking body.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/partner/ledger", {
          params: { booking_reference: reference, limit: 50 },
        });
        if (!cancelled) setLedger(data);
      } catch (e) {
        if (!cancelled) setLedgerError(e?.response?.data?.detail || "Unable to load ledger lines.");
      }
    })();
    return () => { cancelled = true; };
  }, [reference]);

  const onDownloadSummary = async (kind = "summary") => {
    setDownloadError("");
    setDownloading(true);
    try {
      const token = localStorage.getItem("scwt_token");
      const res = await fetch(
        `${BACKEND_URL}/api/partner/reports/${encodeURIComponent(reference)}/${kind === "coa" ? "coa" : "summary"}.pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("Awaiting laboratory review — this report becomes available once the lab publishes the results.");
        }
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "coa" ? `aware-full-analysis-${reference}.pdf` : `summary-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(e?.message || "Unable to download Summary Report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <PartnerShell active="bookings">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="partner-booking-back-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      {loading && (
        <div className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-500" data-testid="partner-booking-detail-loading">
          Loading…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800" data-testid="partner-booking-detail-error">
          {error}
          <div className="mt-3">
            <Link to="/partner/bookings" className="text-sm font-semibold underline">Back to bookings</Link>
          </div>
        </div>
      )}
      {b && !loading && !error && (
        <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="partner-booking-detail">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Booking</div>
          <h1 className="text-2xl font-bold text-slate-900 font-mono" data-testid="partner-booking-detail-reference">{b.reference}</h1>
          <div className="mt-2 empty:hidden">
            <TankCleanContext recommended={b.tank_clean_recommended} context={b.tank_clean_context} testIdPrefix="partner-booking-tank-context" />
          </div>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Customer</div>
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <Row label="Name">{b.customer?.name || "—"}</Row>
                <Row label="Email">{b.customer?.email || "—"}</Row>
                <Row label="Phone">{b.customer?.phone || "—"}</Row>
                <Row label="Address">{b.customer?.address || "—"}</Row>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Booking</div>
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <Row label="Booking date">{fmtDateTime(b.created_at)}</Row>
                <Row label="Test">{b.test_id || "—"}</Row>
                <Row label="Source">{b.booking_source || "Direct"}</Row>
                <Row label="Allocation">{b.allocation_method || "—"}</Row>
                {b.allocation_match_confidence && (
                  <Row label="Match confidence">{b.allocation_match_confidence}</Row>
                )}
                {b.allocation_matched_postcode && (
                  <Row label="Matched postcode">{b.allocation_matched_postcode}</Row>
                )}
                {b.allocation_matched_suburb && (
                  <Row label="Matched suburb">{b.allocation_matched_suburb}</Row>
                )}
                <Row label="Lab status">{b.lab_status || "—"}</Row>
                <Row label="Report status">{b.report_status || "—"}</Row>
                {b.results_published_at && (
                  <Row label="Results published">{fmtDateTime(b.results_published_at)}</Row>
                )}
              </div>
            </div>
          </div>

          {/* Phase 8 — Sample Kit (read-only) */}
          {b.kit_method && (
            <div
              data-testid="partner-booking-kit-panel"
              className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">Sample kit</div>
                <span
                  data-testid="partner-booking-kit-status-chip"
                  className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-white text-slate-700 border border-slate-200"
                >
                  {String(b.kit_status || "pending").replaceAll("_", " ")}
                </span>
                <span
                  data-testid="partner-booking-kit-method-chip"
                  className="text-[10px] px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200"
                >
                  {labelFor(PARTNER_KIT_LABELS, b.kit_method)}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <Row label="Sent">{b.kit_sent_date ? fmtDateTime(b.kit_sent_date) : "—"}</Row>
                <Row label="Sample received">{b.sample_received_date ? fmtDateTime(b.sample_received_date) : "—"}</Row>
                <Row label="Outgoing tracking">{b.kit_tracking_number || "—"}</Row>
                <Row label="Return tracking">{b.return_tracking_number || "—"}</Row>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Kit status is managed by Aware Water Testing. This view is read-only.
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-slate-100 pt-5" data-testid="partner-booking-detail-actions">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reports</div>
                <div className="text-xs text-slate-600 max-w-xl">
                  Download the customer-facing Water Health Summary or the full AWARE
                  laboratory analysis (Certificate of Analysis). Available once the lab
                  releases the results.
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => onDownloadSummary()}
                  disabled={downloading}
                  data-testid="partner-booking-download-summary"
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloading ? "Preparing…" : "Water Health Summary"}
                </button>
                <button
                  type="button"
                  onClick={() => onDownloadSummary("coa")}
                  disabled={downloading}
                  data-testid="partner-booking-download-coa"
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloading ? "Preparing…" : "Full Analysis / COA"}
                </button>
              </div>
            </div>
            {downloadError && (
              <div
                className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5"
                data-testid="partner-booking-download-error"
              >
                {downloadError}
              </div>
            )}
          </div>

          {/* ── Ledger lines for this booking (read-only) ─── */}
          <div
            className="mt-6 rounded-xl bg-white border border-slate-200 overflow-hidden"
            data-testid="partner-booking-ledger-panel"
          >
            <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-cyan-600" />
                <h2 className="text-sm font-bold text-slate-900">Ledger lines for this booking</h2>
                {ledger?.rows && (
                  <span className="text-slate-400 text-xs font-normal">({ledger.rows.length})</span>
                )}
              </div>
              <Link
                to="/partner/ledger"
                data-testid="partner-booking-ledger-open-ledger-link"
                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 hover:underline"
              >
                Open full ledger →
              </Link>
            </header>
            {ledgerError ? (
              <div
                data-testid="partner-booking-ledger-error"
                className="p-4 text-xs text-rose-700 bg-rose-50 border-t border-rose-100"
              >
                {ledgerError}
              </div>
            ) : !ledger ? (
              <div
                data-testid="partner-booking-ledger-loading"
                className="p-6 text-center text-xs text-slate-400"
              >
                Loading ledger lines…
              </div>
            ) : (ledger.rows || []).length === 0 ? (
              <div
                data-testid="partner-booking-ledger-empty"
                className="p-6 text-center text-xs text-slate-500"
              >
                No ledger activity for this booking yet. Commission rows appear here
                once the booking is paid and rolled through the pipeline.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="partner-booking-ledger-table"
                >
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-bold">Date</th>
                      <th className="text-left px-4 py-2 font-bold">Type</th>
                      <th className="text-right px-4 py-2 font-bold">Amount</th>
                      <th className="text-left px-4 py-2 font-bold">Status</th>
                      <th className="text-left px-4 py-2 font-bold">Paid</th>
                      <th className="text-left px-4 py-2 font-bold">Payout ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.rows.map((r) => (
                      <tr
                        key={r.id}
                        data-testid={`partner-booking-ledger-row-${r.id}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-2 text-slate-700 font-mono text-xs">{r.date || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-700">
                          {LEDGER_TYPE_LABEL[r.ledger_type] || r.ledger_type || "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">
                          {AUD.format(r.amount || 0)}
                        </td>
                        <td className="px-4 py-2"><LedgerStatusPill status={r.status} /></td>
                        <td className="px-4 py-2 text-xs text-slate-500 font-mono">{r.paid_at || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-500 font-mono">{r.payout_reference || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500">
              Read-only. Approvals, payments and adjustments are managed by Aware admin.
            </footer>
          </div>

          <div className="mt-6 text-xs text-slate-500" data-testid="partner-booking-detail-disclaimer">
            Internal lab notes, commission details and admin-only fields are not surfaced in the partner view.
          </div>
        </div>
      )}
    </PartnerShell>
  );
}
